import { useRouterState } from '@tanstack/react-router';
import { usePathContext } from '@new/common/hooks/usePathContext';
import {
  useGetDegreeQuery,
  useGetCourseQuery,
  useGetLessonQuery,
  useGetObjectiveQuery,
} from '@teachur/http-client';
import { buildCoursePath, buildLessonPath } from '@new/common/lib/buildPaths';

export interface BreadcrumbItem {
  label: string;
  path: string;
}

const SKIP_ID = 0;

export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const {
    degreeId = SKIP_ID,
    courseId = SKIP_ID,
    lessonId = SKIP_ID,
    objectiveId = SKIP_ID,
  } = usePathContext();

  const { data: degree } = useGetDegreeQuery(degreeId);
  const { data: course } = useGetCourseQuery(courseId);
  const { data: lesson } = useGetLessonQuery(lessonId);
  const { data: objective } = useGetObjectiveQuery(objectiveId);

  const crumbs: BreadcrumbItem[] = [];

  if (pathname === '/courses') {
    crumbs.push({ label: 'Dashboard', path: '/' });
    crumbs.push({ label: 'Courses List', path: '/courses' });
    return crumbs;
  }

  if (degreeId && degree) {
    crumbs.push({ label: 'Degrees', path: '/degrees' });
    crumbs.push({ label: degree.name, path: `/degrees/${degreeId}` });
  }

  if (courseId && !degreeId) {
    crumbs.push({ label: 'Courses List', path: '/courses' });
  }

  if (courseId && course) {
    crumbs.push({
      label: course.name,
      path: buildCoursePath({ degreeId, courseId }),
    });
  }

  if (lessonId && lesson) {
    crumbs.push({
      label: lesson.name,
      path: buildLessonPath({ degreeId, courseId, lessonId }),
    });
  }

  if (objectiveId && objective) {
    crumbs.push({
      label: objective.name,
      path: pathname,
    });
  }

  return crumbs;
};
